import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import useHydrated from '../hooks/useHydrated';
import './MobileHeader.css';

const Chevron = () => (
  <svg className="mobile-header-link-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

// `dock`: on the phone landing page the controls move to a dock at the
// bottom of the screen (within thumb reach) between the demo and the close.
function MobileHeader({ dock = false }) {
  const { isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const hydrated = useHydrated();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [docked, setDocked] = useState(false);
  const dockRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrolled(y > 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!dock) {
      setDocked(false);
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const viewport = window.innerHeight || 1;
      const start = document.querySelector('[data-dock-start]');
      const stop = document.querySelector('[data-dock-stop]');
      const started = start ? start.getBoundingClientRect().bottom < viewport * 0.5 : false;
      const stopped = stop ? stop.getBoundingClientRect().top < viewport * 0.92 : false;
      setDocked(started && !stopped);
      if (dockRef.current) {
        const range = Math.max(1, document.documentElement.scrollHeight - viewport);
        const read = Math.min(1, Math.max(0, (window.scrollY || 0) / range));
        dockRef.current.style.setProperty('--dock-read', read.toFixed(4));
      }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [dock]);

  const showDock = dock && hydrated;
  const dockVisible = showDock && docked && !menuOpen;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (location.pathname !== '/') return;

    const params = new URLSearchParams(location.search);
    const targetId = params.get('section');
    if (!targetId) return;

    const headerOffset = 76;
    const maxAttempts = 20;

    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(targetId);
      if (!el) {
        if (attempt < maxAttempts) {
          window.setTimeout(() => tryScroll(attempt + 1), 80);
        }
        return;
      }

      const targetY = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });

      if (window.history && window.history.replaceState) {
        window.history.replaceState(window.history.state, '', '/');
      }
    };

    window.requestAnimationFrame(() => tryScroll());
  }, [location.pathname, location.search]);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      setMenuOpen(false);
      navigate(`/?section=${encodeURIComponent(id)}`);
      return;
    }

    const el = document.getElementById(id);
    if (!el) {
      setMenuOpen(false);
      return;
    }
    const headerOffset = 76;
    const targetY = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    setMenuOpen(false);
  };

  const handleGoHome = () => {
    setMenuOpen(false);

    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    navigate('/');
  };

  return (
    <>
      {/* The brand is the only control: tapping the medallion opens the menu.
          Once the page scrolls, the wordmark fades and just the mark stays. */}
      <header
        className={`mobile-header ${scrolled ? 'is-scrolled' : ''} ${dockVisible ? 'is-docked' : ''}`}
        inert={dockVisible ? '' : undefined}
      >
        <div className="mobile-header-inner">
          <button
            type="button"
            className="mobile-header-logo"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-header-navigation"
            onClick={() => setMenuOpen(true)}
          >
            <img src="/icons/png/virgil-profile-mark.webp" alt="" className="mobile-header-logo-img" />
            <span className="mobile-header-logo-lockup">
              <span className="mobile-header-logo-wordmark">Virgil</span>
              <svg className="mobile-header-logo-chevron" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        </div>
      </header>

      <div
        id="mobile-header-navigation"
        className={`mobile-header-overlay ${menuOpen ? 'is-open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <div className="mobile-header-overlay-top">
          {/* Same spot, same brand: tapping it again simply closes the sheet
              and leaves the scroll position exactly where it was. */}
          <button
            type="button"
            className="mobile-header-logo mobile-header-overlay-brand"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/icons/png/virgil-profile-mark.webp" alt="" className="mobile-header-logo-img" />
            <span className="mobile-header-logo-lockup">
              <span className="mobile-header-logo-wordmark">Virgil</span>
              <svg className="mobile-header-logo-chevron is-open" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          <div className="mobile-header-overlay-controls">
            <button
              type="button"
              className="mobile-header-overlay-close"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <nav className="mobile-header-overlay-nav">
          {/* Off the landing page, Home is an explicit link rather than a
              side effect of the brand button. */}
          {location.pathname !== '/' && (
            <button
              type="button"
              onClick={handleGoHome}
              className="mobile-header-link-button"
              style={{ '--i': 0 }}
            >
              <span>Home</span>
              <Chevron />
            </button>
          )}
          <button
            type="button"
            onClick={() => scrollToSection('demo-chatbot')}
            className="mobile-header-link-button"
            style={{ '--i': 0 }}
          >
            <span>Demo</span>
            <Chevron />
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('screenshots')}
            className="mobile-header-link-button"
            style={{ '--i': 1 }}
          >
            <span>Features</span>
            <Chevron />
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('intro-copy')}
            className="mobile-header-link-button"
            style={{ '--i': 2 }}
          >
            <span>Mission</span>
            <Chevron />
          </button>
          <a href="/pricing" className="mobile-header-link" style={{ '--i': 3 }}>
            <span>Pricing</span>
            <Chevron />
          </a>
          <a href="/faq" className="mobile-header-link" style={{ '--i': 4 }}>
            <span>FAQ</span>
            <Chevron />
          </a>
          <a href="/download" className="mobile-header-link" style={{ '--i': 5 }}>
            <span>Download</span>
            <Chevron />
          </a>
          {isAuthenticated && (
            <a href="/account" className="mobile-header-link" style={{ '--i': 6 }}>
              <span>Dashboard</span>
              <Chevron />
            </a>
          )}

          <div className="mobile-header-divider" />

          <div className="mobile-header-overlay-actions">
            {!isAuthenticated ? (
              <>
                <button
                  type="button"
                  className="mobile-header-action-btn"
                  onClick={() => {
                    setMenuOpen(false);
                    loginWithRedirect({
                      appState: { returnTo: `${location.pathname}${location.search}` },
                      authorizationParams: { screen_hint: 'login', prompt: 'select_account' },
                    });
                  }}
                >
                  Log in
                </button>
                <button
                  type="button"
                  className="mobile-header-action-btn mobile-header-action-btn-primary"
                  onClick={() => {
                    setMenuOpen(false);
                    window.location.href = '/signup';
                  }}
                >
                  Start 14-day trial
                </button>
              </>
            ) : (
              <button
                type="button"
                className="mobile-header-action-btn"
                onClick={() => {
                  setMenuOpen(false);
                  logout({ logoutParams: { returnTo: window.location.origin } });
                }}
              >
                Log out
              </button>
            )}
          </div>
        </nav>
      </div>

      {showDock && (
        <div
          ref={dockRef}
          className={`mobile-dock ${dockVisible ? 'is-visible' : ''}`}
          inert={dockVisible ? undefined : ''}
        >
          {/* The ring around the mark fills as the page is read. */}
          <button
            type="button"
            className="mobile-dock-menu"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-header-navigation"
            onClick={() => setMenuOpen(true)}
          >
            <img src="/icons/png/virgil-profile-mark.webp" alt="" className="mobile-dock-mark" />
          </button>
          {isAuthenticated ? (
            <a href="/account" className="mobile-dock-cta">
              Dashboard
            </a>
          ) : (
            <a href="/signup" className="mobile-dock-cta">
              Start 14-day trial
            </a>
          )}
        </div>
      )}
    </>
  );
}

export default MobileHeader;
